'use client';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import { Carousel, CarouselContent, CarouselItem } from '@/components/ui/carousel';
import { cn } from '@/lib/utils';

const OurClient = ({ clientsData }) => {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth <= 900
  );
  const [api, setApi] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const screenWidth = window.innerWidth;
      setIsMobile(screenWidth <= 900);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (!api) return;

    const onSelect = () => {
      setSelectedIndex(api.selectedScrollSnap());
      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());
    };

    setScrollSnaps(api.scrollSnapList());
    api.on('select', onSelect);
    api.on('reInit', onSelect);
    onSelect();

    return () => {
      api.off('select', onSelect);
    };
  }, [api]);

  //function to divide the clients array into subArrays
  function createSlidesArr(clients) {
    const slidesArr = [];
    const maxItemsPerSlide = 12;

    const numSlides = Math.ceil(clients.length / maxItemsPerSlide);

    const targetItemsPerSlide = Math.ceil(clients.length / numSlides);

    for (let i = 0; i < numSlides; i++) {
      const startIndex = i * targetItemsPerSlide;
      const endIndex = startIndex + targetItemsPerSlide;
      const slide = clients.slice(startIndex, endIndex);
      slidesArr.push(slide);
    }

    if (slidesArr.length === 1) {
      slidesArr.push(slidesArr[0]);
    }

    return slidesArr;
  }

  const slidesArr = createSlidesArr([...clientsData.clients]);

  return (
    <div className=" clients mx-auto max-w-screen-xl w-full flex flex-col gap-14 items-center justify-center">
      <h2 className="text-4xl font-bold text-white">
        {clientsData?.client_section_heading}
      </h2>
      <div className="w-[80vw] flex flex-wrap items-center justify-center gap-8 lg:gap-16">
        <div className="w-full grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 items-center cursor-grab justify-center gap-4 lg:gap-10">
          <Carousel
            setApi={setApi}
            className="w-[80vw] select-none relative"
            style={{
              padding: '0px 0px 70px 0px',
              minHeight: '50vh',
              display: 'flex',
              alignItems: 'center',
            }}
            opts={{
              loop: false,
            }}
          >
            <CarouselContent className="w-full -ml-8">
              {slidesArr?.map((item, index) => {
                return (
                  <CarouselItem
                    key={index}
                    className="pl-8 basis-full"
                    style={{
                      width: '100%',
                      height: '80%',
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: `${isMobile ? '30px 50px' : '50px 100px'}`,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {item?.map((client) => {
                      return (
                        <div
                          key={client?.client_logo?.key}
                          className=" lg:h-28 h-24 flex flex-wrap flex-col gap-2 justify-end items-center"
                        >
                          <Image
                            src={`${process.env.NEXT_PUBLIC_API_URL}/assets/${client?.client_logo?.key}`}
                            className="md:h-20 h-16 w-24 object-contain items-center"
                            width={500}
                            height={500}
                            sizes="96px"
                            alt={client?.client_name || 'client logo'}
                            loading="lazy"
                          />
                          <p className="text-[#999999] lg:text-sm md:text-sm text-xs">
                            {client?.client_name}
                          </p>
                        </div>
                      );
                    })}
                  </CarouselItem>
                );
              })}
            </CarouselContent>

            {/* Pagination Dots */}
            <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-1 z-10">
              {scrollSnaps.map((_, index) => (
                <button
                  key={index}
                  onClick={() => api?.scrollTo(index)}
                  className="w-6 h-6 flex items-center justify-center"
                  aria-label={`Go to slide ${index + 1}`}
                >
                  <span
                    className={cn(
                      'w-2.5 h-2.5 rounded-full block transition-all duration-300',
                      index === selectedIndex
                        ? 'bg-white opacity-100'
                        : 'bg-[#b9b9b9] opacity-20 hover:opacity-50'
                    )}
                  />
                </button>
              ))}
            </div>

            {/* Navigation Arrows (desktop only) */}
            {!isMobile && scrollSnaps.length > 1 && (
              <>
                <button
                  onClick={() => api?.scrollPrev()}
                  disabled={!canScrollPrev}
                  className={cn(
                    'absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center bg-white text-black transition-all duration-300 shadow-md z-20',
                    !canScrollPrev
                      ? 'opacity-30 cursor-not-allowed'
                      : 'opacity-100 hover:bg-white/90 hover:scale-105 active:scale-95'
                  )}
                  aria-label="Previous slide"
                >
                  <Image
                    src="/icons/arrow-left.svg"
                    width={16}
                    height={16}
                    alt="Prev"
                    className="w-4 h-auto invert"
                  />
                </button>
                <button
                  onClick={() => api?.scrollNext()}
                  disabled={!canScrollNext}
                  className={cn(
                    'absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center bg-white text-black transition-all duration-300 shadow-md z-20',
                    !canScrollNext
                      ? 'opacity-30 cursor-not-allowed'
                      : 'opacity-100 hover:bg-white/90 hover:scale-105 active:scale-95'
                  )}
                  aria-label="Next slide"
                >
                  <Image
                    src="/icons/arrow-right.svg"
                    width={16}
                    height={16}
                    alt="Next"
                    className="w-4 h-auto invert"
                  />
                </button>
              </>
            )}
          </Carousel>
        </div>
      </div>
    </div>
  );
};

export default OurClient;
